import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { patientsApi, requestsApi, assignmentsApi, Patient, PatientOfferStats, LockedPatient } from '../../services/api';
import PatientDetailModal from '../PatientDetailModal/PatientDetailModal';
import OfferPopover from '../OfferPopover/OfferPopover';
import { getRequirementStyle } from '../../config/requirementConfig';
import './PatientListModal.css';

interface PatientListModalProps {
  requestId: string;
  requestStatus: string;
  requestCreatorId?: string;
  requestCategory?: string; // MEDEVAC, PERSONNEL, MATERIAL, OTHER
  onClose: () => void;
  offerPatientIds?: string[]; // Optional: Filter to show only specific patients (for viewing specific offer)
}

function PatientListModal({ requestId, requestStatus, requestCreatorId, requestCategory, onClose, offerPatientIds }: PatientListModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Check if user can make/edit offers (PUBLISHED MEDEVAC request and user is NOT the creator)
  const isUserCreator = requestCreatorId && user?.id === requestCreatorId;
  const isMedevacRequest = requestCategory === 'MEDEVAC' || !requestCategory;
  const canMakeOffer = isMedevacRequest && requestStatus === 'PUBLISHED' && !isUserCreator;

  // Check if viewing specific offer (offerPatientIds provided means we're viewing offer details)
  const isViewingOffer = offerPatientIds && offerPatientIds.length > 0;

  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 500,
    total: 0,
    totalPages: 0
  });

  // Offer Statistics
  const [offerStats, setOfferStats] = useState<Map<string, PatientOfferStats>>(new Map());

  // Mission/Assignment info per patient
  const [patientMissions, setPatientMissions] = useState<Map<string, LockedPatient>>(new Map());

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    dependency: '',
    transport: '',
    requirements: [] as string[],
    hasCompanions: '',
    offerStatus: ''
  });

  // Sorting
  const [sortBy, setSortBy] = useState('patientId');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Requirements dropdown state
  const [requirementsDropdownOpen, setRequirementsDropdownOpen] = useState(false);

  // Patient detail modal state
  const [showPatientDetail, setShowPatientDetail] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  useEffect(() => {
    loadPatients();
  }, [pagination.page, pagination.limit, sortBy, sortOrder, filters]);

  useEffect(() => {
    // Load offer statistics and mission data when modal opens
    loadOfferStatistics();
    loadPatientMissions();
  }, [requestId]);


  const loadOfferStatistics = async () => {
    try {
      const response = await requestsApi.getPatientOffers(requestId);
      const statsMap = new Map<string, PatientOfferStats>();
      response.data.forEach(stat => {
        statsMap.set(stat.patientId, stat);
      });
      setOfferStats(statsMap);
    } catch (error) {
      console.error('Failed to load offer statistics:', error);
    }
  };

  const loadPatientMissions = async () => {
    try {
      const response = await assignmentsApi.getLockedPatients(requestId);
      const missionsMap = new Map<string, LockedPatient>();
      response.data.forEach(lp => {
        missionsMap.set(lp.patientId, lp);
      });
      setPatientMissions(missionsMap);
    } catch (error) {
      // Silent - missions may not exist
    }
  };

  const loadPatients = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder
      };

      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.dependency) params.dependency = filters.dependency;
      if (filters.transport) params.transport = filters.transport;
      if (filters.requirements.length > 0) params.requirements = filters.requirements;
      if (filters.hasCompanions) params.hasCompanions = filters.hasCompanions === 'true';

      const response = await patientsApi.getByRequestId(requestId, params);

      // If offerPatientIds is provided, filter to show only those patients
      let filteredPatients = response.data.patients;
      if (offerPatientIds && offerPatientIds.length > 0) {
        filteredPatients = response.data.patients.filter(p => offerPatientIds.includes(p.id));
      }

      setPatients(filteredPatients);
      setPagination(prev => ({
        ...prev,
        total: offerPatientIds && offerPatientIds.length > 0 ? filteredPatients.length : response.data.pagination.total,
        totalPages: offerPatientIds && offerPatientIds.length > 0 ? Math.ceil(filteredPatients.length / pagination.limit) : response.data.pagination.totalPages
      }));
    } catch (error) {
      console.error('Failed to load patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleFilterChange = (name: string, value: any) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleRequirementToggle = (req: string) => {
    setFilters(prev => ({
      ...prev,
      requirements: prev.requirements.includes(req)
        ? prev.requirements.filter(r => r !== req)
        : [...prev.requirements, req]
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: '',
      dependency: '',
      transport: '',
      requirements: [],
      hasCompanions: '',
      offerStatus: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const activeFilterCount = [
    filters.status, filters.priority, filters.dependency, filters.transport,
    filters.offerStatus, filters.search, filters.hasCompanions
  ].filter(Boolean).length + (filters.requirements.length > 0 ? 1 : 0);

  const renderSortIcon = (field: string) => {
    if (sortBy !== field) return '⇅';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Dynamically extract unique requirements from patients
  const requirements = useMemo(() => {
    const uniqueRequirements = new Set<string>();
    patients.forEach(patient => {
      if (patient.medicalRequirements && Array.isArray(patient.medicalRequirements)) {
        patient.medicalRequirements.forEach((req: any) => {
          if (req.requirementType) {
            uniqueRequirements.add(req.requirementType);
          }
        });
      }
    });
    return Array.from(uniqueRequirements).sort();
  }, [patients]);

  // Get patients to display based on filters and offerPatientIds
  const displayPatients = patients.filter(p => {
    // If offerPatientIds is provided (viewing specific offer), only show those specific patients
    if (offerPatientIds && offerPatientIds.length > 0) {
      if (!offerPatientIds.includes(p.id)) {
        return false;
      }
    }

    // Apply offer status filter (matching PatientSelectionStep logic)
    if (filters.offerStatus) {
      const stats = offerStats.get(p.id);
      const hasTreatment = stats ? (stats.offersByType.TREATMENT > 0 || (stats.offersByType.TREATMENT_ONLY || 0) > 0) : false;
      const hasTransport = stats ? stats.offersByType.TRANSPORT > 0 : false;
      const hasBoth = stats ? (stats.offersByType.BOTH > 0 || (stats.offersByType.TREATMENT_AND_TRANSPORT || 0) > 0) : false;

      switch (filters.offerStatus) {
        case 'FULL_OFFER': if (!(hasBoth || (hasTreatment && hasTransport))) return false; break;
        case 'TREATMENT': if (!(hasTreatment && !hasBoth && !hasTransport)) return false; break;
        case 'NONE': if (!(!hasTreatment && !hasTransport && !hasBoth)) return false; break;
      }
    }
    return true;
  });

  return (
    <div className="patient-list-modal-overlay">
      <div className="patient-list-modal">
        <div className="modal-header">
          <div className="modal-header-left">
            <h2>
              {t('patients.title')}
              {isViewingOffer && <span style={{ color: '#6c757d', fontSize: '14px', fontWeight: 'normal' }}> ({t('offers.offerDetail')})</span>}
            </h2>
            <span className="patient-count-badge">{displayPatients.length} {t('patients.title').toLowerCase()}</span>
          </div>
          <div className="modal-header-right">
            {canMakeOffer && (
              <button
                className="btn btn-success"
                onClick={() => {
                  onClose();
                  navigate(`/requests/${requestId}/offers/create`);
                }}
              >
                {t('offers.makeOffer')}
              </button>
            )}
            <button className="modal-close" onClick={onClose}><X size={18} /></button>
          </div>
        </div>

        <div className="modal-body">
          {/* Sticky Toolbar - matching Step 1 style */}
          <div className="sticky-toolbar">
            {/* Row 1: Filters */}
            <div className="toolbar-row toolbar-filters">
              <input
                type="text"
                placeholder={t('components.dataTable.search')}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="filter-input"
                style={{ width: '180px' }}
              />

              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="filter-select"
              >
                <option value="">{t('common.priority')}</option>
                <option value="P1">P1</option>
                <option value="P2">P2</option>
                <option value="P3">P3</option>
                <option value="TBA">TBA</option>
              </select>

              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="filter-select"
              >
                <option value="">{t('common.status')}</option>
                <option value="CIVIL">Civil</option>
                <option value="MILITARY">Military</option>
                <option value="TBA">TBA</option>
              </select>

              <select
                value={filters.dependency}
                onChange={(e) => handleFilterChange('dependency', e.target.value)}
                className="filter-select"
              >
                <option value="">{t('patients.mobility')}</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="TBA">TBA</option>
              </select>

              <select
                value={filters.transport}
                onChange={(e) => handleFilterChange('transport', e.target.value)}
                className="filter-select"
              >
                <option value="">{t('offers.transport')}</option>
                <option value="SEATED">{t('patients.seated')}</option>
                <option value="LYING">{t('patients.lying')}</option>
                <option value="TBA">TBA</option>
              </select>

              <select
                value={filters.offerStatus}
                onChange={(e) => handleFilterChange('offerStatus', e.target.value)}
                className="filter-select"
              >
                <option value="">{t('offers.title')}</option>
                <option value="FULL_OFFER">{t('offers.fullOffer')}</option>
                <option value="TREATMENT">{t('offers.treatment')}</option>
                <option value="NONE">{t('common.none')}</option>
              </select>

              <div className="requirements-dropdown">
                <button
                  className="dropdown-toggle"
                  onClick={() => setRequirementsDropdownOpen(!requirementsDropdownOpen)}
                  type="button"
                >
                  Req. {filters.requirements.length > 0 && `(${filters.requirements.length})`}
                </button>
                {requirementsDropdownOpen && (
                  <div className="dropdown-menu">
                    {requirements.map(req => {
                      const style = getRequirementStyle(req);
                      return (
                        <label key={req} className="dropdown-item">
                          <input
                            type="checkbox"
                            checked={filters.requirements.includes(req)}
                            onChange={() => handleRequirementToggle(req)}
                          />
                          <span
                            className="badge-requirement"
                            style={{ background: style.bg, color: style.color, padding: '2px 6px', borderRadius: '3px', fontSize: '11px', fontWeight: 700 }}
                          >
                            {style.label}
                          </span>
                          <span>{req.replace(/_/g, ' ')}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {activeFilterCount > 0 && (
                <button onClick={handleResetFilters} className="btn-reset" title={t('common.reset')}>
                  {t('common.reset')} ({activeFilterCount})
                </button>
              )}
            </div>

            {/* Row 2: Patient count + Pagination */}
            <div className="toolbar-row toolbar-actions">
              <span className="selection-info">
                {pagination.total} {t('patients.title').toLowerCase()}
              </span>

              <span className="toolbar-separator" />

              {/* Pagination */}
              <div className="toolbar-pagination">
                <button
                  className="btn-page"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  title="Previous page"
                >
                  &lt;
                </button>
                <span className="page-info">
                  {pagination.page}/{pagination.totalPages}
                </span>
                <button
                  className="btn-page"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  title="Next page"
                >
                  &gt;
                </button>
                <select
                  className="page-size"
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={250}>250</option>
                  <option value={500}>500</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-scroll-area">
            {loading ? (
              <div className="loading">{t('common.loading')}</div>
            ) : (
              <table className="patient-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('patientId')} className="sortable">
                      {t('patients.patientId')} {renderSortIcon('patientId')}
                    </th>
                    <th>{t('patients.firstName')}</th>
                    <th onClick={() => handleSort('gender')} className="sortable">
                      {t('patients.gender')} {renderSortIcon('gender')}
                    </th>
                    <th onClick={() => handleSort('age')} className="sortable">
                      {t('patients.age')} {renderSortIcon('age')}
                    </th>
                    <th onClick={() => handleSort('status')} className="sortable" style={{ width: '32px' }}>
                      {renderSortIcon('status')}
                    </th>
                    <th onClick={() => handleSort('conditionId')} className="sortable">
                      {t('patients.condition')} {renderSortIcon('conditionId')}
                    </th>
                    <th>{t('patients.diagnosis')}</th>
                    <th>{t('patients.treatmentNeeded')}</th>
                    <th onClick={() => handleSort('transport')} className="sortable" style={{ width: '40px' }}>
                      {renderSortIcon('transport')}
                    </th>
                    <th onClick={() => handleSort('dependency')} className="sortable">
                      {t('patients.mobility')} {renderSortIcon('dependency')}
                    </th>
                    <th onClick={() => handleSort('priority')} className="sortable">
                      {t('common.priority')} {renderSortIcon('priority')}
                    </th>
                    <th style={{ width: '36px' }}></th>
                    <th>{t('offers.title')}</th>
                    <th>{t('offers.transport')}</th>
                    <th>{t('common.nation')}</th>
                  </tr>
                </thead>
                <tbody>
                  {displayPatients.length === 0 ? (
                    <tr>
                      <td colSpan={15} className="no-data">
                        {t('patients.noPatients')}
                      </td>
                    </tr>
                  ) : (
                    displayPatients.map(patient => (
                      <tr key={patient.id}>
                        <td>
                          {patient.patientId ? (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setSelectedPatient(patient);
                                setShowPatientDetail(true);
                              }}
                              style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
                            >
                              {patient.patientId}
                            </a>
                          ) : (
                            <span className="badge badge-pending">{t('status.pending')}</span>
                          )}
                        </td>
                        <td>{patient.patientInitials}</td>
                        <td>{patient.patientGender || '-'}</td>
                        <td>{patient.patientAge}</td>
                        <td>
                          <span className={`status-circle status-${patient.patientStatus.toLowerCase()}`}>
                            {patient.patientStatus === 'CIVIL' ? 'C' : patient.patientStatus === 'MILITARY' ? 'M' : '?'}
                          </span>
                        </td>
                        <td>{patient.medicalPrimaryConditionId}</td>
                        <td className="diagnosis-cell" title={patient.medicalPrimaryConditionDiagnosis}>
                          {patient.medicalPrimaryConditionDiagnosis}
                        </td>
                        <td>
                          {patient.medicalRequirements && patient.medicalRequirements.length > 0 ? (
                            <div className="requirements-badges">
                              {patient.medicalRequirements.map((req: any) => {
                                const style = getRequirementStyle(req.requirementType);
                                return (
                                  <span
                                    key={req.id}
                                    className="badge-requirement"
                                    style={{ background: style.bg, color: style.color }}
                                    title={req.requirementType.replace(/_/g, ' ')}
                                  >
                                    {style.label}
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          {patient.medicalTransport === 'LYING' && (
                            <img src="/icons/patient-liegend.webp" alt="Lying" width={20} height={20} title="Lying" />
                          )}
                          {patient.medicalTransport === 'SEATED' && (
                            <img src="/icons/patient-sitzend.webp" alt="Seated" width={20} height={20} title="Seated" />
                          )}
                          {patient.medicalTransport === 'TBA' && (
                            <span>&#10067; TBA</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge badge-dependency-${patient.medicalDependency.toLowerCase()}`}>
                            {patient.medicalDependency}
                          </span>
                        </td>
                        <td>
                          <span className={`badge badge-priority-${patient.medicalPriority.toLowerCase()}`}>
                            {patient.medicalPriority}
                          </span>
                        </td>
                        <td>
                          {patient.patientCompanionsCount > 0 && (
                            <span className="companions-indicator">
                              <img src="/icons/benutzer.webp" alt="Companions" width={16} height={16} />
                              <span>{patient.patientCompanionsCount}</span>
                            </span>
                          )}
                        </td>
                        <td>
                          {(() => {
                            const stats = offerStats.get(patient.id);
                            if (!stats || stats.offerCount === 0) {
                              return (
                                <span className="offer-icons" title="No offers">
                                  <img src="/icons/delivery_leer.webp" alt="No Transport" width={16} height={16} />
                                  <img src="/icons/hospital_leer.webp" alt="No Treatment" width={16} height={16} />
                                </span>
                              );
                            }
                            const hasFull = (stats.offersByType.BOTH > 0) || ((stats.offersByType.TREATMENT_AND_TRANSPORT || 0) > 0);
                            const hasTreatment = stats.offersByType.TREATMENT > 0 || (stats.offersByType.TREATMENT_ONLY || 0) > 0;
                            const hasTransport = stats.offersByType.TRANSPORT > 0;
                            const hasTransportExt = (stats.offersByType.TRANSPORT_EXTENSION || 0) > 0;
                            // Check if any offer entry has an external transport nation (via transportProviderNationId)
                            const hasExtTransportNation = stats.offersByNation?.some(
                              o => o.transportNation && o.transportNation !== o.nation
                            ) || false;

                            // Build tooltip parts
                            const tooltipParts: string[] = [];
                            if (hasFull) tooltipParts.push(`Full Offer: ${(stats.offersByType.BOTH || 0) + (stats.offersByType.TREATMENT_AND_TRANSPORT || 0)}`);
                            if (hasTransport) tooltipParts.push(`Ext. Transport: ${stats.offersByType.TRANSPORT}`);
                            if (hasTreatment) tooltipParts.push(`Treatment: ${(stats.offersByType.TREATMENT || 0) + (stats.offersByType.TREATMENT_ONLY || 0)}`);
                            if (hasTransportExt) tooltipParts.push(`Provide Transport: ${stats.offersByType.TRANSPORT_EXTENSION}`);
                            const tooltip = `${stats.offerCount} offer(s): ${tooltipParts.join(', ')}`;

                            // Determine icon to show
                            let icon: React.ReactNode;
                            if (hasFull) {
                              icon = (
                                <span className="offer-icons" title={tooltip}>
                                  <img src="/icons/offer_combined_gruen.webp" alt="Full Offer" width={32} height={18} />
                                </span>
                              );
                            } else if ((hasTransport || hasExtTransportNation) && hasTreatment) {
                              icon = (
                                <span className="offer-icons" title={tooltip}>
                                  <img src="/icons/delivery_gelb.webp" alt="Ext. Transport" width={16} height={16} />
                                  <img src="/icons/hospital_gruen.webp" alt="Treatment" width={16} height={16} />
                                </span>
                              );
                            } else if (hasTreatment) {
                              icon = (
                                <span className="offer-icons" title={tooltip}>
                                  <img src="/icons/delivery_leer.webp" alt="No Transport" width={16} height={16} />
                                  <img src="/icons/hospital_gruen.webp" alt="Treatment" width={16} height={16} />
                                </span>
                              );
                            } else if (hasTransport || hasTransportExt) {
                              icon = (
                                <span className="offer-icons" title={tooltip}>
                                  <img src="/icons/delivery_gruen.webp" alt="Transport" width={16} height={16} />
                                  <img src="/icons/hospital_leer.webp" alt="No Treatment" width={16} height={16} />
                                </span>
                              );
                            } else {
                              icon = (
                                <span className="offer-icons" title={tooltip}>
                                  <img src="/icons/delivery_leer.webp" alt="No Transport" width={16} height={16} />
                                  <img src="/icons/hospital_leer.webp" alt="No Treatment" width={16} height={16} />
                                </span>
                              );
                            }

                            return (
                              <OfferPopover offers={stats.offersByNation || []}>
                                {icon}
                              </OfferPopover>
                            );
                          })()}
                        </td>
                        <td>
                          {(() => {
                            const stats = offerStats.get(patient.id);
                            if (!stats || !stats.offersByNation) return <span style={{ color: '#adb5bd', fontSize: '11px' }}>-</span>;
                            const transportNations = stats.offersByNation
                              .filter(o => o.transportNation)
                              .map(o => o.transportNation!);
                            const unique = Array.from(new Set(transportNations));
                            if (unique.length === 0) return <span style={{ color: '#adb5bd', fontSize: '11px' }}>-</span>;
                            return (
                              <span className="transport-provider-badges">
                                {unique.map(nation => (
                                  <span key={nation} className="transport-provider-badge">{nation}</span>
                                ))}
                              </span>
                            );
                          })()}
                        </td>
                        <td>
                          {(() => {
                            const missionInfo = patientMissions.get(patient.id);
                            if (!missionInfo) return <span style={{ color: '#adb5bd', fontSize: '11px' }}>-</span>;
                            return (
                              <div className="patient-mission-info">
                                {missionInfo.mission && (
                                  <span className="patient-mission-id" title={`Mission: ${missionInfo.mission.missionId}`}>
                                    {missionInfo.mission.missionId}
                                  </span>
                                )}
                                {missionInfo.treatmentNation && (
                                  <span className="patient-mission-nation" title="Treatment Nation">
                                    {missionInfo.treatmentNation}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

        </div>


        {/* Patient Detail Modal */}
        {showPatientDetail && selectedPatient && (
          <PatientDetailModal
            patient={selectedPatient}
            onClose={() => {
              setShowPatientDetail(false);
              setSelectedPatient(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

export default PatientListModal;
